/**
 * ادغامِ پروندهٔ پرسنلیِ تکراری.
 *
 * خواستهٔ کاربر: «نسترن پناهی یا نسترن درزی جفتشون یکی‌ن… آقای خادم هم سرایداره
 * هم وسط‌کار… این دو تا رو یکی کن، کسی اگه کاری کرده اینا رو همه رو با هم ادغام
 * کن که تکرار توشون نباشه».
 *
 * جریان: فهرستِ نامزدها ← انتخابِ اینکه کدام پرونده بماند ← **پیش‌نمایشِ دقیق**
 * (چند ردیف از کدام جدول جابه‌جا می‌شود، کدام فیلد پر می‌شود، چه نقشی به
 * «شغلِ دوم» تبدیل می‌شود) ← تأیید. تا پیش از تأیید هیچ چیزی تغییر نمی‌کند.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Button, Collapse, Descriptions, Empty, Modal, Popconfirm, Radio, Space, Spin, Table, Tag, Typography, message,
} from "antd";
import { SwapOutlined, UndoOutlined, UsergroupDeleteOutlined } from "@ant-design/icons";
import Alert from "./GxAlert";
import {
  getDuplicateEmployees, markEmployeesDistinct, mergeEmployees, previewEmployeeMerge, unmarkEmployeesDistinct,
} from "../api/client";
import { apiError } from "../utils/apiError";
import { faNum } from "../utils/faNumberFormat";
import { formatMoney } from "../utils/money";

const { Text } = Typography;

const who = (p) => `${p.name}${p.code ? ` (${p.code})` : ""}`;

export default function MergeDuplicatesModal({ open, onClose, onMerged }) {
  const [pairs, setPairs] = useState([]);
  const [distinct, setDistinct] = useState([]);   // v2.162.0: جفت‌هایی که «جدا هستند» علامت خورده‌اند
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState(null);      // {keep_id, drop_id}
  const [plan, setPlan] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await getDuplicateEmployees();
      setPairs(r.data?.pairs || []);
      setDistinct(r.data?.distinct || []);
    } catch (e) { message.error(apiError(e, "خطا در یافتنِ تکراری‌ها")); }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!open) { setSel(null); setPlan(null); return; }
    load();
  }, [open, load]);

  const choose = async (keep, drop) => {
    setSel({ keep_id: keep.id, drop_id: drop.id });
    setPlan(null);
    setBusy(true);
    try {
      const r = await previewEmployeeMerge({ keep_id: keep.id, drop_id: drop.id });
      setPlan(r.data);
    } catch (e) { message.error(apiError(e, "پیش‌نمایش ساخته نشد")); }
    setBusy(false);
  };

  /* v2.162.0 (کاربر): «فاطمه اسماعیلی دوتا داریم، جدا هستند — گزینه‌ای باشد که جدا از هم‌اند و نیازی به یکی‌کردن نیست» */
  const markDistinct = async (r) => {
    try {
      await markEmployeesDistinct(r.a.id, r.b.id);
      message.success(`«${who(r.a)}» و «${who(r.b)}» جدا علامت خوردند و دیگر پیشنهاد نمی‌شوند`);
      if (sel && [r.a.id, r.b.id].includes(sel.keep_id)) { setSel(null); setPlan(null); }
      await load();
    } catch (e) { message.error(apiError(e, "ثبت نشد")); }
  };
  const unmark = async (d) => {
    try { await unmarkEmployeesDistinct(d.id); await load(); }
    catch (e) { message.error(apiError(e, "انجام نشد")); }
  };

  const doMerge = async () => {
    if (!sel || !plan) return;
    setBusy(true);
    try {
      const r = await mergeEmployees({ ...sel, drop_conflicting: !plan.safe });
      message.success(r.data?.message || "ادغام شد");
      setSel(null); setPlan(null);
      await load();
      onMerged?.();
    } catch (e) { message.error(apiError(e, "ادغام انجام نشد")); }
    setBusy(false);
  };

  return (
    <Modal open={open} onCancel={onClose} footer={null} width={880}
      title="پرونده‌های تکراریِ پرسنل">
      <Alert type="info" showIcon style={{ marginBottom: 12 }}
        message="یک نفر باید یک پرونده داشته باشد"
        description="اگر کسی چند نقش دارد، لازم نیست چند پرونده داشته باشد؛ نقشِ دومش بعد از ادغام به‌صورتِ «شغلِ دوم» با مبلغِ خودش روی همان پرونده می‌نشیند. حضور، تولید، مساعده و هر ردیفِ دیگری هم منتقل می‌شود." />

      <Table size="small" rowKey={(r) => `${r.a.id}-${r.b.id}`} loading={loading}
        dataSource={pairs} pagination={false} scroll={{ y: 220 }}
        locale={{ emptyText: <Empty description="پروندهٔ تکراری‌ای پیدا نشد" /> }}
        columns={[
          { title: "پرونده A", render: (_, r) => (
            <Space size={4}><span>{who(r.a)}</span>
              {r.a.job_title && <Tag>{r.a.job_title}</Tag>}
              {r.a.status !== "active" && <Tag color="default">غیرفعال</Tag>}</Space>) },
          { title: "پرونده B", render: (_, r) => (
            <Space size={4}><span>{who(r.b)}</span>
              {r.b.job_title && <Tag>{r.b.job_title}</Tag>}
              {r.b.status !== "active" && <Tag color="default">غیرفعال</Tag>}</Space>) },
          { title: "کدام بماند؟", width: 330, render: (_, r) => (
            <Space size={6}>
              <Radio.Group size="small" buttonStyle="solid"
                value={sel && sel.keep_id === r.a.id ? "a"
                  : sel && sel.keep_id === r.b.id ? "b" : null}
                onChange={(e) => (e.target.value === "a"
                  ? choose(r.a, r.b) : choose(r.b, r.a))}>
                <Radio.Button value="a">A بماند</Radio.Button>
                <Radio.Button value="b">B بماند</Radio.Button>
              </Radio.Group>
              <Popconfirm title="این دو نفر جدا از هم‌اند؟ دیگر به‌عنوانِ تکراری پیشنهاد نمی‌شوند (برگشت‌پذیر)."
                okText="بله، جدا هستند" cancelText="خیر" onConfirm={() => markDistinct(r)}>
                <Button size="small" icon={<UsergroupDeleteOutlined />}>جدا هستند</Button>
              </Popconfirm>
            </Space>) },
        ]} />

      {distinct.length > 0 && (
        <Collapse size="small" style={{ marginTop: 10 }} items={[{
          key: "d", label: `جفت‌هایی که «جدا هستند» علامت خورده‌اند (${faNum(distinct.length)})`,
          children: (
            <Space direction="vertical" style={{ width: "100%" }} size={4}>
              {distinct.map((d) => (
                <Space key={d.id} size={6}>
                  <span>{who(d.a)}</span><Text type="secondary">≠</Text><span>{who(d.b)}</span>
                  <Button size="small" type="link" icon={<UndoOutlined />} onClick={() => unmark(d)}>برگردان</Button>
                </Space>))}
            </Space>),
        }]} />
      )}

      {busy && !plan && <div style={{ textAlign: "center", padding: 24 }}><Spin /></div>}

      {plan && (
        <div style={{ marginTop: 16 }}>
          <Descriptions bordered size="small" column={2}
            title={<span>می‌ماند: <b>{who(plan.keep)}</b> — حذف می‌شود: <b>{who(plan.drop)}</b></span>}>
            <Descriptions.Item label="ردیف‌های منتقل‌شونده" span={2}>
              {plan.total_rows
                ? <Space wrap>{plan.moves.map((m) => (
                    <Tag key={m.table} color={m.conflicts ? "orange" : "blue"}>
                      {m.table}: {faNum(m.rows)}
                      {m.conflicts ? ` (${faNum(m.conflicts)} تعارض)` : ""}
                    </Tag>))}</Space>
                : <Text type="secondary">هیچ ردیفی به این پرونده وصل نیست</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="نقش‌هایی که «شغلِ دوم» می‌شوند" span={2}>
              {plan.new_secondary_roles?.length
                ? <Space wrap>{plan.new_secondary_roles.map((r, i) => (
                    <Tag color="green" key={i}>{r.title} — {formatMoney(r.amount)} ریال</Tag>))}</Space>
                : <Text type="secondary">نقشِ حقوقیِ جدایی ندارد</Text>}
            </Descriptions.Item>
            <Descriptions.Item label="فیلدهایی که پر می‌شوند" span={2}>
              {Object.keys(plan.fills || {}).length
                ? <Space wrap>{Object.entries(plan.fills).map(([k, v]) => (
                    <Tag key={k}>{k}: {v}</Tag>))}</Space>
                : <Text type="secondary">—</Text>}
            </Descriptions.Item>
            {plan.freed_code && (
              <Descriptions.Item label="کدِ آزادشونده" span={2}>
                <Tag color="purple">{plan.freed_code}</Tag>
                <Text type="secondary"> بعد از ادغام قابلِ استفادهٔ دوباره است</Text>
              </Descriptions.Item>)}
          </Descriptions>

          {!plan.safe && (
            <Alert type="warning" showIcon style={{ marginTop: 12 }}
              message="ردیفِ هم‌تاریخ در هر دو پرونده هست"
              description={`ردیفِ پروندهٔ حذف‌شونده در این جدول‌ها کنار گذاشته می‌شود: ${
                plan.conflicts.map((c) => `${c.table} (${c.rows})`).join("، ")}. ردیفِ پروندهٔ باقی‌مانده دست‌نخورده می‌ماند.`} />
          )}

          <Space style={{ marginTop: 12 }}>
            <Button type="primary" danger icon={<SwapOutlined />} loading={busy}
              onClick={() => Modal.confirm({
                title: "ادغام انجام شود؟",
                content: `پروندهٔ «${who(plan.drop)}» حذف و همه‌چیزش به «${who(plan.keep)}» منتقل می‌شود. این کار برگشت‌پذیر نیست.`,
                okText: "بله، ادغام کن", cancelText: "انصراف",
                okButtonProps: { danger: true },
                onOk: doMerge,
              })}>
              ادغام کن
            </Button>
            <Button onClick={() => { setSel(null); setPlan(null); }}>انصراف</Button>
          </Space>
        </div>
      )}
    </Modal>
  );
}
